import axios from "axios";
import { toast } from "react-toastify";
import { signOutRequest } from "../store/auth/authSlice";
import { BaseURL } from "../config/apiUrl.js";
import moment from "moment";
import { apiHeader } from "@/helper/HelperFunction";

const getHeaders = (accessToken) => ({
  Accept: "application/json",
  Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
  "ngrok-skip-browser-warning": "69420",
  "timezone-offset": moment().utcOffset(),
});

const handleError = (error, showAlert, dispatch) => {
  let errorMessage = "";
  if (Array.isArray(error?.response?.data?.message?.error)) {
    error?.response?.data?.message?.error?.forEach(
      (item) => (errorMessage += ` • ${item} \n`)
    );
  } else {
    errorMessage = error?.response?.data?.message?.error;
  }

  if (error.response?.status === 401) {
    dispatch && dispatch(signOutRequest());
  }

  if (showAlert) {
    const message =
      error.message === "Network Error"
        ? `${error.message} : Please Check Your Network Connection`
        : errorMessage;
    toast.error(message, { position: "top-center" });
  }
};

export const Get = async (route, accessToken, showAlert = true, dispatch) => {
  const options = { headers: getHeaders(accessToken) };
  try {
    const response = await axios.get(route, options);
    return response;
  } catch (error) {
    handleError(error, showAlert, dispatch);
  }
};

const apiRequest = async (
  method,
  route,
  data,
  { headers },
  showAlert,
  dispatch
) => {
  const options = {
    method,
    url: route,
    ...(data && { data }),
    headers,
  };
  try {
    return await axios(options);
  } catch (error) {
    handleError(error, showAlert, dispatch);
  }
};

export const Post = (route, data, headers, showAlert = true, dispatch) => {
  const apiHeaders = headers || { headers: getHeaders() };
  return apiRequest("post", route, data, apiHeaders, showAlert, dispatch);
};

export const Patch = (route, data, headers, showAlert = true, dispatch) => {
  const apiHeaders = headers || { headers: getHeaders() };
  return apiRequest("patch", route, data, apiHeaders, showAlert, dispatch);
};

export const Put = (route, data, headers, showAlert = true, dispatch) => {
  const apiHeaders = headers || { headers: getHeaders() };
  return apiRequest("put", route, data, apiHeaders, showAlert, dispatch);
};

export const Delete = (route, headers, showAlert = true, dispatch) => {
  const apiHeaders = headers || { headers: getHeaders() };
  return apiRequest("delete", route, null, apiHeaders, showAlert, dispatch);
};
// The type S3 serves the file with. The signed URL is bound to it, so the
// signing request and the upload must send the same value.
const fileMimeType = (file) => {
  if (file?.type) return file.type;
  const extension = file?.name?.split(".").pop()?.toLowerCase();
  return extension === "pdf" ? "application/pdf" : extension;
};

const UploadFilesToAwsBySignedUrl = async (urlsArray, files) => {
  const promises = urlsArray.map((url, index) => {
    return axios.put(url, files[index], {
      headers: {
        "Content-Type": fileMimeType(files[index]),
        "ngrok-skip-browser-warning": "69420",
      },
    });
  });
  const response = await Promise.allSettled(promises);
  return response;
};
export const uploadMedia = async (selectedFiles, access_token) => {
  if (selectedFiles?.length === 0) return [];
  const params = {
    files: selectedFiles?.map((ele) => ({
      mimeType: fileMimeType(ele),
      name: ele?.name,
    })),
  };
  const apiUrl = BaseURL("get-signed-url");
  const response = await Post(apiUrl, params, apiHeader(access_token));
  console.log("response100", response);
  if (response) {
    if (response?.data?.urls?.length > 0) {
      await UploadFilesToAwsBySignedUrl(response?.data?.urls, selectedFiles);
    }
    return response?.data?.keys;
  }
  return null;
};

/**
 * Uploads one file straight to S3 and resolves with its key.
 * - folder: "videos" | "thumbnails" | "resources" - the key's prefix
 * - onProgress: called with 0-100 while the bytes go up
 * Rejects (after toasting) when signing or the upload fails, so a caller
 * never saves a key that points at nothing.
 */
export const uploadFile = async (
  file,
  { access_token, folder, onProgress } = {},
) => {
  const name = file?.name || `upload.${file?.type?.split("/")[1] || "bin"}`;
  const mimeType = fileMimeType(file) || "application/octet-stream";
  const response = await Post(
    BaseURL("get-signed-url"),
    { files: [{ mimeType, name }], ...(folder && { folder }) },
    apiHeader(access_token),
  );
  const url = response?.data?.urls?.[0];
  const key = response?.data?.keys?.[0];
  if (!url || !key) throw new Error("Could not start the upload");

  try {
    await axios.put(url, file, {
      headers: { "Content-Type": mimeType },
      onUploadProgress: (event) => {
        if (onProgress && event.total) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      },
    });
  } catch (error) {
    toast.error("Upload failed. Check your connection and try again.", {
      position: "top-center",
    });
    throw error;
  }
  return key;
};
